/**
 * Zentrale JSON-LD-Schema-Definitionen.
 *
 * Konsistenzregeln:
 * - Alle URLs absolut (https://…)
 * - Organization.founder <-> Person.affiliation bidirektional (gleiche name + url)
 * - sameAs enthält ausschliesslich real existierende Profile/Websites
 * - contactPoint verweist nur auf die Kontaktseite (keine E-Mail/Telefon)
 * - addressCountry immer als ISO-Code
 */

export const SITE_URL = "https://banking.4tagewoche.org";

export const ORG_NAME = "Intraprenör";
export const ORG_URL = SITE_URL;
export const ORG_LOGO = `${SITE_URL}/logo.png`;
export const CONTACT_PAGE_URL = `${SITE_URL}/business-case`;

const ORG_DESCRIPTION =
  "Intraprenör begleitet Banken und Finanzinstitute bei der Einführung der 4-Tage-Woche: Business Cases, Pilotdesign und Umsetzung.";

/** Real existierende Websites der Initiative (keine Platzhalter). */
const ORG_SAME_AS = [
  "https://www.4tagewoche.org",
  "https://4-tagewoche.ch",
  "https://www.4-tagewoche.at",
];

const AREA_SERVED = ["DE", "CH", "AT"].map((code) => ({
  "@type": "Country",
  name: code,
}));

/** Minimal-Organization für Rückverweise (kein Duplikat der Vollangaben). */
const orgRef = {
  "@type": "Organization",
  name: ORG_NAME,
  url: ORG_URL,
};

type PersonSeed = {
  name: string;
  jobTitle: string;
  url: string;
  sameAs: string[];
  knowsAbout: string[];
};

export const PEOPLE: PersonSeed[] = [
  {
    name: "Carsten Meier",
    jobTitle: "Gründer & Berater",
    url: "https://www.4tagewoche.org",
    sameAs: ["https://www.linkedin.com/in/carsten-meier-36058566/"],
    knowsAbout: [
      "4-Tage-Woche",
      "Arbeitszeitmodelle",
      "Organisationsentwicklung",
      "New Work",
      "Change Management",
    ],
  },
  {
    name: "Veit Hailperin",
    jobTitle: "Gründer & Berater",
    url: "https://www.hailperin.ch",
    sameAs: ["https://www.linkedin.com/in/veit-hailperin/"],
    knowsAbout: [
      "4-Tage-Woche",
      "Bankwesen",
      "Produktivitätsanalyse",
      "Arbeitszeitmodelle",
      "Digitale Transformation",
    ],
  },
];

/** Gründer-Referenzen, wie sie im Organization-Schema erscheinen. */
const founderRefs = PEOPLE.map((p) => ({
  "@type": "Person",
  name: p.name,
  url: p.url,
  sameAs: p.sameAs,
}));


export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: ORG_NAME,
  url: ORG_URL,
  logo: ORG_LOGO,
  description: ORG_DESCRIPTION,
  sameAs: [...ORG_SAME_AS, ORG_URL],
  founder: founderRefs,
  areaServed: AREA_SERVED,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      areaServed: ["DE", "CH", "AT"],
      availableLanguage: ["de", "fr", "en"],
      url: CONTACT_PAGE_URL,
    },
  ],
};

/** Person-Schema mit Rückverweis auf die Organisation (affiliation.founder). */
export function personSchema(person: PersonSeed) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: person.name,
    jobTitle: person.jobTitle,
    url: person.url,
    sameAs: person.sameAs,
    knowsAbout: person.knowsAbout,

    worksFor: orgRef,
    affiliation: {
      ...orgRef,
      founder: founderRefs,
    },
  };
}

export const personSchemas = PEOPLE.map(personSchema);

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "4-Tage-Woche im Bankenwesen",
  url: SITE_URL,
  description:
    "Whitepaper und Report: Wie Banken mit der 4-Tage-Woche den Fachkräftemangel lösen.",
  publisher: orgRef,
  inLanguage: ["de", "fr", "en"],
};

/** Startseite: Organization + Person(en) + WebSite. */
export const homeJsonLd = [organizationSchema, ...personSchemas, websiteSchema];

/** Service-Seiten: Service mit provider-Verweis auf Organisation und Personen. */
export function serviceJsonLd(input: {
  name: string;
  description: string;
  path: string;
}) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: input.name,
      description: input.description,
      url: `${SITE_URL}${input.path}`,
      serviceType: "Business Case Analyse 4-Tage-Woche",
      provider: {
        ...orgRef,
        logo: ORG_LOGO,
        founder: founderRefs,
      },
      areaServed: AREA_SERVED,
      availableLanguage: ["de", "fr", "en"],
      audience: { "@type": "Audience", audienceType: "Banken und Finanzinstitute" },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "CHF",
        url: `${SITE_URL}${input.path}`,
      },
    },
  ];
}

/** Blog/Artikel: Article mit author-Verweis auf die Person(en). */
export function articleJsonLd(input: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
}) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: input.headline,
      description: input.description,
      url: `${SITE_URL}${input.path}`,
      mainEntityOfPage: `${SITE_URL}${input.path}`,
      datePublished: input.datePublished,
      dateModified: input.dateModified ?? input.datePublished,
      author: founderRefs,
      publisher: { ...orgRef, logo: ORG_LOGO },
    },
  ];
}
